<template>
  <el-dialog
      :model-value="modelValue"
      :title="title"
      :width="width"
      :top="top"
      :append-to-body="true"
      :close-on-click-modal="true"
      class="guide-carousel-dialog"
      @update:model-value="(val) => emit('update:modelValue', val)"
  >
    <div v-if="slides.length" class="guide-carousel-wrap">
      <!-- 左箭头：位于轮播图左侧，垂直居中于图片；到首张禁用并悬停提示 -->
      <div v-if="slides.length > 1" class="guide-carousel__arrow-col">
        <el-tooltip
            :content="reachStartText"
            placement="top"
            :disabled="!isFirst"
        >
          <div
              class="guide-carousel__arrow guide-carousel__arrow--left"
              :class="{ 'is-disabled': isFirst }"
              @click="handlePrev"
          >
            <el-icon>
              <ArrowLeft/>
            </el-icon>
          </div>
        </el-tooltip>
      </div>

      <div class="guide-carousel__col">
        <el-carousel
            ref="carouselRef"
            :height="height"
            :autoplay="false"
            :loop="false"
            arrow="never"
            :indicator-position="slides.length > 1 ? 'outside' : 'none'"
            trigger="click"
            @change="handleChange"
        >
          <el-carousel-item v-for="(item, index) in slides" :key="index">
            <div v-if="item.image" class="guide-slide__media">
              <el-image
                  :src="item.image"
                  fit="contain"
                  class="guide-slide__img"
                  :preview-src-list="[item.image]"
                  preview-teleported
                  hide-on-click-modal
              >
                <template #error>
                  <div class="guide-slide__img-error">
                    <el-icon>
                      <Picture/>
                    </el-icon>
                  </div>
                </template>
              </el-image>
            </div>
          </el-carousel-item>
        </el-carousel>
      </div>

      <!-- 右箭头：位于轮播图右侧，垂直居中于图片；到尾张禁用并悬停提示 -->
      <div v-if="slides.length > 1" class="guide-carousel__arrow-col">
        <el-tooltip
            :content="reachEndText"
            placement="top"
            :disabled="!isLast"
        >
          <div
              class="guide-carousel__arrow guide-carousel__arrow--right"
              :class="{ 'is-disabled': isLast }"
              @click="handleNext"
          >
            <el-icon>
              <ArrowRight/>
            </el-icon>
          </div>
        </el-tooltip>
      </div>
    </div>
    <!-- 文字说明：位于轮播图及进度点下方，固定高度避免弹窗高度随内容变化 -->
    <div v-if="activeSlide" class="guide-slide__text">
      <h3 v-if="activeSlide.title" class="guide-slide__title">{{ activeSlide.title }}</h3>
      <p v-if="activeSlide.desc" class="guide-slide__desc">{{ activeSlide.desc }}</p>
    </div>
    <!-- 不再弹出：底部居中 -->
    <div v-if="neverAgainText" class="guide-slide__footer">
      <el-button size="small" type="primary" @click="handleNeverAgain">{{ neverAgainText }}</el-button>
    </div>
  </el-dialog>
</template>

<script setup>
import {defineProps, defineEmits, ref, computed} from "vue";
import {Picture, ArrowLeft, ArrowRight} from "@element-plus/icons-vue";
import {i18n} from "@/lang";

const props = defineProps({
  // 弹窗显隐，配合 v-model 使用
  modelValue: {
    type: Boolean,
    default: false,
  },
  // 弹窗标题
  title: {
    type: String,
    default: "",
  },
  // 轮播数据：[{ image, title, desc }]
  slides: {
    type: Array,
    default: () => [],
  },
  // 弹窗宽度
  width: {
    type: [String, Number],
    default: "600px",
  },
  // 弹窗距顶部距离（默认较靠上）
  top: {
    type: String,
    default: "6vh",
  },
  // 轮播区域高度
  height: {
    type: String,
    default: "360px",
  },
  // 文字说明区域固定高度（避免弹窗高度随内容变化）
  textHeight: {
    type: String,
    default: "96px",
  },
  // “不再弹出”按钮文案，为空时不展示该按钮
  neverAgainText: {
    type: String,
    default: "",
  },
  // 已到第一张时的悬停提示
  reachStartText: {
    type: String,
    default: () => i18n.global.t("common.guide.reach.start"),
  },
  // 已到最后一张时的悬停提示
  reachEndText: {
    type: String,
    default: () => i18n.global.t("common.guide.reach.end"),
  },
});

const emit = defineEmits(["update:modelValue", "never-again"]);

// 当前轮播下标，用于同步展示对应文字说明
const carouselRef = ref(null);
const activeIndex = ref(0);
const handleChange = (index) => {
  activeIndex.value = index;
};
const activeSlide = computed(() => props.slides?.[activeIndex.value] ?? null);

// 边界判断：首/尾张时对应箭头禁用（保留但不可点击）
const isFirst = computed(() => activeIndex.value <= 0);
const isLast = computed(() => activeIndex.value >= props.slides.length - 1);

// 上一张/下一张：到边界不做处理（由 tooltip 提示）
const handlePrev = () => {
  if (isFirst.value) return;
  carouselRef.value?.prev();
};
const handleNext = () => {
  if (isLast.value) return;
  carouselRef.value?.next();
};

// 点击“不再弹出”：通知父组件持久化，并关闭弹窗
const handleNeverAgain = () => {
  emit("never-again");
  emit("update:modelValue", false);
};
</script>

<style lang="less" scoped>
.guide-carousel-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.guide-carousel__col {
  flex: 1;
  min-width: 0;
}

.guide-carousel__arrow-col {
  flex: 0 0 auto;
  // 高度与图片区域一致，用 flex 让箭头垂直居中于图片
  height: v-bind(height);
  display: flex;
  align-items: center;
}

.guide-carousel__arrow {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(31, 45, 61, 0.45);
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
}

.guide-carousel__arrow:hover {
  background: rgba(31, 45, 61, 0.65);
}

.guide-carousel__arrow.is-disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: rgba(31, 45, 61, 0.45);
}

.guide-slide__media {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  border-radius: 6px;
  overflow: hidden;
}

.guide-slide__img {
  width: 100%;
  height: 100%;
  cursor: zoom-in;
}

.guide-slide__img-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #c0c4cc;
  font-size: 40px;
}

.guide-slide__text {
  width: 100%;
  height: v-bind(textHeight);
  padding: 0 12px 8px;
  box-sizing: border-box;
  text-align: center;
  overflow-y: auto;
}

.guide-slide__title {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  line-height: 22px;
}

.guide-slide__desc {
  margin: 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 20px;
  word-break: break-word;
  white-space: pre-line;
  text-align: left;
}

.guide-slide__footer {
  display: flex;
  justify-content: center;
  padding: 8px 12px 4px;
}
</style>
